import React, {Fragment} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';
import {v4} from 'uuid';

import {DebouncedInput} from 'app/general/primitives/DebouncedInput';
import {gt} from 'app/utils/i18n';

import {FieldLastEditStamp} from './FieldLastEditStamp';
import {ReorderOrDeleteButton} from './ReorderOrDeleteButton';

import type {StepEditFieldProps, StepTaskFieldProps} from './commonprops';
import type {
  AttachedFieldChoiceTypes,
  AttachedStepFieldType,
} from 'sweet-types';

type StepViewGenericChoicesFieldProps = {
  field: AttachedStepFieldType;
  inputType: AttachedFieldChoiceTypes;
};

export const StepViewGenericChoicesField = ({
  field,
  inputType,
}: StepViewGenericChoicesFieldProps) => (
  <div className="form-group row mb-0">
    <div className="col-12">
      {field.label !== '' ? (
        <legend className="col-form-label pt-0">{field.label}</legend>
      ) : null}
      {field.type === 'checkbox' || field.type === 'radio' ? (
        <div className={`${(field.choices || []).length > 1 ? 'small' : ''}`}>
          {field.choices.map(({label, id, changedState}) => (
            <StyledFormCheckLabel className={cs(changedState)} key={id}>
              <DisabledCheckbox
                className="form-check-input"
                checked={false}
                type={inputType}
                readOnly={true}
              />
              <div className="form-check-label d-inline-block">{label}</div>
            </StyledFormCheckLabel>
          ))}
        </div>
      ) : null}
    </div>
  </div>
);

type StepTaskGenericChoicesFieldProps = StepTaskFieldProps & {
  inputType: AttachedFieldChoiceTypes;
};

export const StepTaskGenericChoicesField = ({
  field,
  inputValueChanged,
  isSaving,
  progress,
  value,
  blocked,
  inputType,
}: StepTaskGenericChoicesFieldProps) => (
  <div className="form-group row mb-0">
    <div
      className={cs('col-12', {
        'col-md-8':
          (field.type === 'checkbox' || field.type === 'radio') &&
          field.choices.length === 1,
      })}
    >
      {field.label !== '' ? (
        <legend className="col-form-label pt-0">{field.label}</legend>
      ) : null}
      {field.type === 'checkbox' || field.type === 'radio' ? (
        <div className={`${(field.choices || []).length > 1 ? 'small' : ''}`}>
          {field.choices.map(({label, id}) => (
            <StyledHoverableFormCheckLabel key={id}>
              <input
                className="form-check-input"
                type={inputType}
                checked={
                  inputType === 'radio'
                    ? value === id
                    : value.indexOf(id) !== -1
                }
                onChange={() => inputValueChanged(id)}
                disabled={isSaving || blocked}
              />
              <div className="form-check-label d-inline-block">{label}</div>
            </StyledHoverableFormCheckLabel>
          ))}
        </div>
      ) : null}
    </div>
    <div
      className={cs('col-12 d-flex align-items-center justify-content-end', {
        'col-md-4':
          (field.type === 'checkbox' || field.type === 'radio') &&
          field.choices.length === 1,
      })}
    >
      <FieldLastEditStamp progress={progress} />
    </div>
  </div>
);

type StepEditGenericChoicesFieldProps = StepEditFieldProps & {
  inputType: AttachedFieldChoiceTypes;
};

export function StepEditGenericChoicesField({
  label,
  onChange,
  optionsButton,
  choices,
  onChangeChoices,
  inputType,
}: StepEditGenericChoicesFieldProps): JSX.Element {
  const changeChoiceLabel = (choiceId: string, label: string) => {
    if (choices != null) {
      return onChangeChoices(
        choices.map((choice) =>
          choice.id !== choiceId ? choice : {...choice, label}
        )
      );
    }
  };
  const addChoice = () => {
    return onChangeChoices([
      ...(choices || []),
      {
        id: v4(),
        label: '',
      },
    ]);
  };
  const removeChoice = (choiceId: string) => {
    if (choices != null) {
      return onChangeChoices(
        choices.filter((choice) => choice.id !== choiceId)
      );
    }
  };
  const reorderChoice = (from: number, to: number) => {
    const updatedChoices = [...(choices ?? [])];
    const element = updatedChoices[from];
    updatedChoices.splice(from, 1);
    updatedChoices.splice(to, 0, element);
    return onChangeChoices(updatedChoices);
  };

  return (
    <Fragment>
      <div className="input-group">
        <DebouncedInput
          className="form-control"
          type="text"
          value={label}
          onChange={onChange}
          placeholder={
            inputType === 'checkbox'
              ? gt('Checkbox field label...')
              : gt('Radio field label...')
          }
        />
        {optionsButton}
      </div>
      {choices != null
        ? choices.map(({label, id}, index) => (
            <div className="input-group input-group-sm" key={id}>
              <div className="input-group-prepend">
                <div className="input-group-text">
                  <input disabled type={inputType} />
                </div>
              </div>
              <DebouncedInput
                className="form-control"
                type="text"
                value={label}
                onChange={(value) => changeChoiceLabel(id, value)}
                placeholder={gt('Choice...')}
              />
              <ReorderOrDeleteButton
                small={true}
                itemIndex={index}
                numItems={choices.length}
                onReorder={reorderChoice}
                canRemove={choices.length > 1}
                onRemove={() => removeChoice(id)}
              />
            </div>
          ))
        : null}
      <button className="btn btn-sm btn-link" onClick={addChoice}>
        <i className="fas fa-plus" /> {gt('Add another choice')}
      </button>
    </Fragment>
  );
}

export const StyledFormCheckLabel = styled.label.attrs<{className?: string}>({
  className: 'form-check py-1 my-0',
})`
  border-radius: 4px;
  padding-right: 1.25rem;

  .form-check-input {
    margin-top: 0.25em;
    margin-right: -1.25rem;
  }

  &.ins {
    background-color: ${colors.insGreen};
  }

  &.del {
    background-color: ${colors.delRed};
  }
`;

export const DisabledCheckbox = styled.input`
  opacity: 0.3;
`;

const StyledHoverableFormCheckLabel = styled(StyledFormCheckLabel)`
  &:hover {
    background: ${colors.paleBlue};
  }
`;
